import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCreatePage } from '../../hooks/usePage';
import { fetchPages, updatePageReorderOptimistic, movePage } from '../../redux/Slices/pageSlice';
import { showError, getErrorMessage } from '../../utils/snackbarHelpers';
import PageSidebarItem from './PageSidebarItem';
import SkeletonSidebar from '../Skeletons/SkeletonSidebar';
import Tooltip from '../Global/Tooltip';
import {
  Add,
  Star,
  StarOutline,
  Person,
  Business,
  People,
  Delete,
  ExpandMore,
  ChevronRight,
  Diamond,
  Share,
  Archive,
} from '../icons';
import './scss/PageSidebar.scss';
import Heart from '../icons/Heart';

/**
 * Notion-style Page Sidebar
 * Sections: Favorites, Personal, Workspace, Shared with me, Trash
 * Can work with or without a specific workspaceId
 */
const PageSidebar = ({ workspaceId: propWorkspaceId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { workspaceId: paramWorkspaceId } = useParams();
  const workspaceId = propWorkspaceId || paramWorkspaceId;
  const createPageMutation = useCreatePage();

  const user = useSelector(state => state.user);

  // Get pages from Redux
  const { byId, allIds } = useSelector(state => state.pages);
  const pages = allIds.map(id => byId[id]).filter(Boolean);

  // Section collapse states
  const [sectionsCollapsed, setSectionsCollapsed] = useState({
    favorites: false,
    personal: false,
    workspace: false,
    shared: false,
    archived: true, // Archived collapsed by default
    trash: true // Trash collapsed by default
  });

  const [isLoading, setIsLoading] = useState(false);

  const loadPages = async () => {
    setIsLoading(true);
    try {
      // 1. Fetch active pages first (Fast)
      // This will populate the main sections of the sidebar quickly
      const activeParams = { 
        workspaceId, 
        includeDeleted: false, 
        archived: 'false',
        merge: false 
      };
      await dispatch(fetchPages(activeParams)).unwrap();
      setIsLoading(false); // Stop loading skeleton after active pages are in

      // 2. Fetch all pages (including trash/archive) in background (Slower)
      // This will fill in the Trash and Archived sections
      const allParams = { 
        workspaceId, 
        includeDeleted: true, 
        archived: 'all',
        merge: true 
      };
      dispatch(fetchPages(allParams));
    } catch (error) {
      console.error('Error loading pages:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, [workspaceId, dispatch]);

  // Helper to sort pages by order property
  const sortByOrder = (a, b) => {
    const orderA = a.order ?? 0;
    const orderB = b.order ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return new Date(a.createdAt) - new Date(b.createdAt);
  };

  // Get current user ID for ownership check
  const currentUserId = user?.user?._id || user?.user?.id;

  // Helper to check if a page is shared with the current user
  const checkIsShared = (p) => {
    if (p.isShared !== undefined) return p.isShared;
    const ownerId = p.ownerUserId?._id || p.ownerUserId;
    return currentUserId && ownerId && ownerId.toString() !== currentUserId.toString();
  };

  // Categorize and sort pages
  const categorizedPages = {
    favorites: pages.filter(p => p.isFavorite && !p.deletedAt && !p.archived).sort((a, b) => {
      const orderA = a.favoriteOrder ?? 0;
      const orderB = b.favoriteOrder ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      return new Date(a.createdAt) - new Date(b.createdAt);
    }),
    personal: pages.filter(p => !p.workspaceId && !p.deletedAt && !p.parentId && !checkIsShared(p) && !p.archived).sort(sortByOrder),
    workspace: pages.filter(p => p.workspaceId && !p.deletedAt && !p.parentId && !checkIsShared(p) && !p.archived).sort(sortByOrder),
    shared: pages.filter(p => checkIsShared(p) && !p.deletedAt && !p.archived).sort(sortByOrder),
    archived: pages.filter(p => p.archived && !p.deletedAt).sort(sortByOrder),
    trash: pages.filter(p => p.deletedAt).sort((a, b) => new Date(b.deletedAt) - new Date(a.deletedAt))
  };

  const toggleSection = (section) => {
    setSectionsCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCreatePage = async (type = 'personal') => {
    if (createPageMutation.isLoading) return; // guard against double-submit
    try {
      const pageData = {
        title: '',
        icon: '📄',
        workspaceId: type === 'workspace' ? workspaceId : null,
        type: 'doc'
      };

      const result = await createPageMutation.mutateAsync(pageData);

      // After creation, we just need to ensure the parent is expanded or the new page is in the list
      // The createPage thunk already adds the page to the store, but a split fetch ensures consistency
      dispatch(fetchPages({ workspaceId, includeDeleted: false, archived: 'false', merge: true }));
      // Navigate to the new page
      if (result?.page?.shortId) {
        navigate(`/p/${result.page.shortId}`);
      }
    } catch (error) {
      console.error('Failed to create page:', error);
      showError(dispatch, getErrorMessage(error, "Couldn't create the page. Please try again."));
    }
  };

  if (isLoading && pages.length === 0) {
    return <SkeletonSidebar />;
  }

  return (
    <div className="page-sidebar">
      <Tooltip />
      {/* Favorites Section */}
      <div className="page-sidebar-section">
        <div
          className="page-sidebar-section-header"
          onClick={() => toggleSection('favorites')}
        >
          <div className="section-left">
            <div className="section-icon">
              <Heart size={18} />
            </div>
            <span className="section-title">Favorites</span>
          </div>
          <div className="section-actions">
            <button 
              className={`section-toggle ${sectionsCollapsed.favorites ? 'collapsed' : ''}`}
            >
              <ExpandMore size={20} />
            </button>
          </div>
        </div>

        {!sectionsCollapsed.favorites && (
          <div className="page-sidebar-section-content">
            {categorizedPages.favorites.length === 0 ? (
              <div className="page-sidebar-empty">
                <span>No favorites yet</span>
              </div>
            ) : (
              categorizedPages.favorites.map(page => (
                <PageSidebarItem
                  key={page._id}
                  page={page}
                  allPages={pages}
                  workspaceId={workspaceId}
                  level={0}
                  section="favorites"
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Personal Pages Section */}
      <div className="page-sidebar-section">
        <div
          className="page-sidebar-section-header"
          onClick={() => toggleSection('personal')}
        >
          <div className="section-left">
            <div className="section-icon">
              <Person size={18} />

            </div>
            <span className="section-title">Personal</span>
          </div>
          <div className="section-actions">
            <button
              className="section-add-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleCreatePage('personal');
              }}
              disabled={createPageMutation.isLoading}
              data-tooltip-content="Add personal page"
              data-tooltip-position="top"
            >
              <Add size={16} />
            </button>
            <button 
              className={`section-toggle ${sectionsCollapsed.personal ? 'collapsed' : ''}`}
            >
              <ExpandMore size={20} />
            </button>
          </div>
        </div>

        {!sectionsCollapsed.personal && (
          <div className="page-sidebar-section-content">
            {categorizedPages.personal.length === 0 ? (
              <button
                type="button"
                className="page-sidebar-empty page-sidebar-empty--action"
                onClick={() => handleCreatePage('personal')}
                disabled={createPageMutation.isLoading}
              >
                <span>+ Create your first page</span>
              </button>
            ) : (
              categorizedPages.personal.map(page => (
                <PageSidebarItem
                  key={page._id}
                  page={page}
                  allPages={pages}
                  workspaceId={workspaceId}
                  level={0}
                  section="personal"
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Workspace Pages Section */}
      {workspaceId && (
        <div className="page-sidebar-section">
          <div
            className="page-sidebar-section-header"
            onClick={() => toggleSection('workspace')}
          >
            <div className="section-left">
              <div className="section-icon">
                <Diamond size={18} />
              </div>
              <span className="section-title">Workspace</span>
            </div>
            <div className="section-actions">
              <button
                className="section-add-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreatePage('workspace');
                }}
                disabled={createPageMutation.isLoading}
                data-tooltip-content="Add workspace page"
                data-tooltip-position="top"
              >
                <Add size={16} />
              </button>
              <button 
                className={`section-toggle ${sectionsCollapsed.workspace ? 'collapsed' : ''}`}
              >
                <ExpandMore size={20} />
              </button>
            </div>
          </div>

          {!sectionsCollapsed.workspace && (
            <div className="page-sidebar-section-content">
              {categorizedPages.workspace.length === 0 ? (
                <div className="page-sidebar-empty">
                  <span>No workspace pages</span>
                </div>
              ) : (
                categorizedPages.workspace.map(page => (
                  <PageSidebarItem
                    key={page._id}
                    page={page}
                    allPages={pages}
                    workspaceId={workspaceId}
                    level={0}
                    section="workspace"
                  />
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Shared with Me Section */}
      <div className="page-sidebar-section">
        <div
          className="page-sidebar-section-header"
          onClick={() => toggleSection('shared')}
        >
          <div className="section-left">
            <div className="section-icon">
              <Share size={18} />
            </div>
            <span className="section-title">Shared with me</span>
          </div>
          <div className="section-actions">
            <button 
              className={`section-toggle ${sectionsCollapsed.shared ? 'collapsed' : ''}`}
            >
              <ExpandMore size={20} />
            </button>
          </div>
        </div>

        {!sectionsCollapsed.shared && (
          <div className="page-sidebar-section-content">
            {categorizedPages.shared.length === 0 ? (
              <div className="page-sidebar-empty">
                <span>No shared pages</span>
              </div>
            ) : (
              categorizedPages.shared.map(page => (
                <PageSidebarItem
                  key={page._id}
                  page={page}
                  allPages={pages}
                  workspaceId={workspaceId}
                  level={0}
                  section="shared"
                />
              ))
            )}
          </div>
        )}
      </div>
      
      {/* Archived Section */}
      <div className="page-sidebar-section">
        <div
          className="page-sidebar-section-header"
          onClick={() => toggleSection('archived')}
        >
          <div className="section-left">
            <div className="section-icon">
              <Archive size={18} />
            </div>
            <span className="section-title">Archived</span>
          </div>
          <div className="section-actions">
            {categorizedPages.archived.length > 0 && (
              <span className="section-count">{categorizedPages.archived.length}</span>
            )}
            <button 
              className={`section-toggle ${sectionsCollapsed.archived ? 'collapsed' : ''}`}
            >
              <ExpandMore size={20} />
            </button>
          </div>
        </div>

        {!sectionsCollapsed.archived && (
          <div className="page-sidebar-section-content">
            {categorizedPages.archived.length === 0 ? (
              <div className="page-sidebar-empty">
                <span>No archived pages</span>
              </div>
            ) : (
              categorizedPages.archived.map(page => (
                <PageSidebarItem
                  key={page._id}
                  page={page}
                  allPages={pages}
                  workspaceId={workspaceId}
                  level={0}
                  isArchived={true}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Trash Section */}
      <div className="page-sidebar-section">
        <div
          className="page-sidebar-section-header"
          onClick={() => toggleSection('trash')}
        >
          <div className="section-left">
            <div className="section-icon">
              <Delete size={18} />
            </div>
            <span className="section-title">Trash</span>
          </div>
          <div className="section-actions">
            {categorizedPages.trash.length > 0 && (
              <span className="section-count">{categorizedPages.trash.length}</span>
            )}
            <button 
              className={`section-toggle ${sectionsCollapsed.trash ? 'collapsed' : ''}`}
            >
              <ExpandMore size={20} />
            </button>
          </div>
        </div>

        {!sectionsCollapsed.trash && (
          <div className="page-sidebar-section-content">
            {categorizedPages.trash.length === 0 ? (
              <div className="page-sidebar-empty">
                <span>Trash is empty</span>
              </div>
            ) : (
              categorizedPages.trash.map(page => (
                <PageSidebarItem
                  key={page._id}
                  page={page}
                  allPages={pages}
                  workspaceId={workspaceId}
                  level={0}
                  isTrash={true}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageSidebar;
